import {
  useEffect,
  useMemo,
  useState,
} from 'react';

import {
  Drawer,
  LoadingState,
} from '@/components/base';

import type {
  GeneratedDocument,
  GeneratedDocumentVersion,
} from '@/hooks/useGeneratedDocuments';

import {
  getGeneratedDocumentSourceLabel,
  getGeneratedDocumentStatusLabel,
  getGeneratedDocumentVersionLabel,
} from '@/lib/generatedDocumentPresentation';

type HistoryResult = {
  generatedDocument:
    GeneratedDocument;

  versions:
    GeneratedDocumentVersion[];
};

type Props = {
  document:
    GeneratedDocument | null;

  isOpen:
    boolean;

  onClose:
    () => void;

  loadHistory:
    (
      id: string
    ) =>
      Promise<HistoryResult>;
};

const formatDateTime = (
  value:
    string |
    null |
    undefined
) => {
  if (!value) {
    return '—';
  }

  const parsed =
    new Date(value);

  if (
    Number.isNaN(
      parsed.getTime()
    )
  ) {
    return value;
  }

  return parsed
    .toLocaleString(
      'pt-BR',
      {
        day:
          '2-digit',

        month:
          '2-digit',

        year:
          'numeric',

        hour:
          '2-digit',

        minute:
          '2-digit',
      }
    );
};

export default function DocumentHistoryDrawer({
  document,
  isOpen,
  onClose,
  loadHistory,
}: Props) {
  const [
    versions,
    setVersions,
  ] =
    useState<
      GeneratedDocumentVersion[]
    >([]);

  const [
    selectedVersionNumber,
    setSelectedVersionNumber,
  ] =
    useState<
      number | null
    >(null);

  const [
    loading,
    setLoading,
  ] =
    useState(false);

  const [
    error,
    setError,
  ] =
    useState<
      string | null
    >(null);

  useEffect(
    () => {
      if (
        !isOpen ||
        !document
      ) {
        return;
      }

      let cancelled =
        false;

      setLoading(true);
      setError(null);
      setVersions([]);
      setSelectedVersionNumber(
        document.currentVersion
      );

      void loadHistory(
        document.id
      )
        .then(
          (result) => {
            if (
              cancelled
            ) {
              return;
            }

            setVersions(
              result.versions
            );

            setSelectedVersionNumber(
              result
                .generatedDocument
                .currentVersion
            );
          }
        )
        .catch(
          (
            caught:
              unknown
          ) => {
            if (
              cancelled
            ) {
              return;
            }

            setError(
              caught instanceof
                Error
                ? caught.message
                : 'Não foi possível carregar o histórico.'
            );
          }
        )
        .finally(
          () => {
            if (
              !cancelled
            ) {
              setLoading(
                false
              );
            }
          }
        );

      return () => {
        cancelled = true;
      };
    },
    [
      document,
      isOpen,
      loadHistory,
    ]
  );

  const selectedVersion =
    useMemo(
      () =>
        versions.find(
          (version) =>
            version.versionNumber ===
            selectedVersionNumber
        ) ??
        versions[0] ??
        null,
      [
        selectedVersionNumber,
        versions,
      ]
    );

  return (
    <Drawer
      isOpen={
        isOpen &&
        Boolean(document)
      }
      onClose={
        onClose
      }
      title={
        document
          ? `Histórico: ${document.title}`
          : 'Histórico do documento'
      }
      width="w-[980px]"
    >
      {!document ? null : loading ? (
        <LoadingState message="Carregando histórico e revisões..." />
      ) : error ? (
        <div className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          <i className="ri-alert-line mr-2"></i>
          {error}
        </div>
      ) : versions.length === 0 ? (
        <div className="rounded-lg border border-background-200 bg-background-50 px-4 py-6 text-center text-sm text-foreground-500">
          Nenhuma versão histórica encontrada.
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-[300px_minmax(0,1fr)] gap-4">
          <div className="space-y-2">
            <div className="mb-3">
              <p className="text-xs font-semibold text-foreground-700">
                Linha do tempo
              </p>

              <p className="text-[11px] text-foreground-400 mt-0.5">
                {versions.length} versão(ões) preservada(s)
              </p>
            </div>

            {versions.map(
              (version) => {
                const selected =
                  selectedVersion
                    ?.versionNumber ===
                  version.versionNumber;

                return (
                  <button
                    key={
                      version.id
                    }
                    type="button"
                    onClick={() =>
                      setSelectedVersionNumber(
                        version.versionNumber
                      )
                    }
                    className={`w-full text-left px-3 py-3 rounded-xl border transition-colors cursor-pointer ${
                      selected
                        ? 'border-primary-300 bg-primary-50'
                        : 'border-background-200 bg-background-50 hover:border-primary-200'
                    }`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <strong className="text-sm text-foreground-800">
                        {getGeneratedDocumentVersionLabel(
                          version.versionNumber
                        )}
                      </strong>

                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-background-100 text-foreground-600">
                        {getGeneratedDocumentStatusLabel(
                          version.status
                        )}
                      </span>
                    </div>

                    <p className="text-xs text-foreground-600 mt-1.5 line-clamp-2">
                      {version.changeNote ??
                        'Revisão documental'}
                    </p>

                    <div className="flex items-center justify-between gap-2 mt-2 text-[10px] text-foreground-400">
                      <span>
                        {getGeneratedDocumentSourceLabel(
                          version.source,
                          Boolean(
                            version.templateId
                          )
                        )}
                      </span>

                      <span>
                        {formatDateTime(
                          version.createdAt
                        )}
                      </span>
                    </div>
                  </button>
                );
              }
            )}
          </div>

          {selectedVersion && (
            <div className="border border-background-200 rounded-xl p-4 min-w-0">
              <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3 mb-4">
                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="text-base font-semibold text-foreground-900">
                      {selectedVersion.title}
                    </h3>

                    <span className="px-2 py-0.5 rounded-full bg-violet-50 text-violet-700 text-[10px] font-semibold">
                      {getGeneratedDocumentVersionLabel(
                        selectedVersion.versionNumber
                      )}
                    </span>
                  </div>

                  <p className="text-xs text-foreground-500 mt-1">
                    {selectedVersion.changeNote ??
                      'Revisão documental'}
                  </p>
                </div>

                <span className="px-2 py-1 rounded-full bg-background-100 text-foreground-600 text-[10px] font-medium">
                  {getGeneratedDocumentStatusLabel(
                    selectedVersion.status
                  )}
                </span>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
                <div>
                  <span className="block text-[10px] uppercase tracking-wide text-foreground-400">
                    Origem
                  </span>

                  <p className="text-sm text-foreground-700">
                    {getGeneratedDocumentSourceLabel(
                      selectedVersion.source,
                      Boolean(
                        selectedVersion.templateId
                      )
                    )}
                  </p>
                </div>

                <div>
                  <span className="block text-[10px] uppercase tracking-wide text-foreground-400">
                    Modelo
                  </span>

                  <p className="text-sm text-foreground-700">
                    {selectedVersion.templateId
                      ? 'Vinculado'
                      : 'Sem modelo'}
                  </p>
                </div>

                <div>
                  <span className="block text-[10px] uppercase tracking-wide text-foreground-400">
                    Versão do modelo
                  </span>

                  <p className="text-sm text-foreground-700">
                    {selectedVersion.templateVersionNumber
                      ? `v${selectedVersion.templateVersionNumber}`
                      : '—'}
                  </p>
                </div>

                <div>
                  <span className="block text-[10px] uppercase tracking-wide text-foreground-400">
                    Registrada em
                  </span>

                  <p className="text-sm text-foreground-700">
                    {formatDateTime(
                      selectedVersion.createdAt
                    )}
                  </p>
                </div>
              </div>

              {selectedVersion.finalizedAt && (
                <div className="mb-4 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-800">
                  <i className="ri-lock-line mr-1.5"></i>
                  Esta revisão foi finalizada em{' '}
                  <strong>
                    {formatDateTime(
                      selectedVersion.finalizedAt
                    )}
                  </strong>.
                </div>
              )}

              <div className="mb-4">
                <span className="block text-[10px] uppercase tracking-wide text-foreground-400 mb-1">
                  Conteúdo registrado nesta versão
                </span>

                <div className="min-h-[220px] max-h-[420px] overflow-y-auto px-3 py-3 border border-background-200 rounded-lg bg-background-50 text-sm text-foreground-800 whitespace-pre-wrap">
                  {selectedVersion.renderedContent ||
                    selectedVersion.content ||
                    'Sem conteúdo.'}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <span className="block text-[10px] uppercase tracking-wide text-foreground-400 mb-1">
                    Observações
                  </span>

                  <div className="min-h-[80px] px-3 py-2 rounded-lg border border-background-200 text-sm text-foreground-700 whitespace-pre-wrap">
                    {selectedVersion.notes ||
                      'Sem observações.'}
                  </div>
                </div>

                <div>
                  <span className="block text-[10px] uppercase tracking-wide text-foreground-400 mb-1">
                    Snapshot de variáveis
                  </span>

                  <div className="max-h-[180px] overflow-y-auto rounded-lg border border-background-200">
                    {Object.entries(
                      selectedVersion.variablesSnapshot
                    ).length > 0 ? (
                      Object.entries(
                        selectedVersion.variablesSnapshot
                      ).map(
                        ([
                          key,
                          value,
                        ]) => (
                          <div
                            key={
                              key
                            }
                            className="flex items-start justify-between gap-3 px-3 py-2 border-b last:border-b-0 border-background-100 text-xs"
                          >
                            <span className="font-mono text-foreground-500">
                              {key}
                            </span>

                            <span className="text-right text-foreground-700 break-words">
                              {value ||
                                '—'}
                            </span>
                          </div>
                        )
                      )
                    ) : (
                      <div className="px-3 py-3 text-xs text-foreground-400">
                        Nenhuma variável armazenada.
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </Drawer>
  );
}